import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useClerk, useUser } from "@clerk/clerk-react";
import { ChevronDown, Menu, X } from "lucide-react";
import useStore from "../../store/useStore";
import "./Header.css";

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user: storeUser, resetAllData, clearUser } = useStore();
  const { signOut } = useClerk();
  const { isSignedIn, user: clerkUser } = useUser();
  
  const [itineraryDropdown, setItineraryDropdown] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileItineraryOpen, setMobileItineraryOpen] = useState(false);
  const [profileDropdown, setProfileDropdown] = useState(false);
  const [headerHidden, setHeaderHidden] = useState(false);

  const dropdownRef = useRef(null);
  const profileDropdownRef = useRef(null);
  const lastScrollY = useRef(0);

  // Hide header on scroll down, show on scroll up.
  // Works for both window scroll and .itinerary-main scroll container.
  useEffect(() => {
    const THRESHOLD = 60;

    const handleScroll = (scrollTop) => {
      const diff = scrollTop - lastScrollY.current;
      if (scrollTop < THRESHOLD) {
        setHeaderHidden(false);
      } else if (diff > 4) {
        setHeaderHidden(true);
      } else if (diff < -4) {
        setHeaderHidden(false);
      }
      lastScrollY.current = scrollTop;
    };

    const onWindow = () => handleScroll(window.scrollY);
    window.addEventListener("scroll", onWindow, { passive: true });

    const scrollEl = document.querySelector(".itinerary-main");
    const onEl = (e) => handleScroll(e.currentTarget.scrollTop);
    if (scrollEl) scrollEl.addEventListener("scroll", onEl, { passive: true });

    return () => {
      window.removeEventListener("scroll", onWindow);
      if (scrollEl) scrollEl.removeEventListener("scroll", onEl);
    };
  }, [location.pathname]);
  
  const userName = isSignedIn ? (clerkUser?.firstName || storeUser?.firstName || "User") : "Guest";

  // Close dropdowns on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setItineraryDropdown(false);
      }
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
        setProfileDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setHeaderHidden(false);
    lastScrollY.current = 0;
  }, [location.pathname]);

  const handleLogout = async () => {
    try {
      resetAllData();
      clearUser();
      localStorage.clear();

      setProfileDropdown(false);
      
      await signOut({ redirectUrl: "/" });
    } catch (error) {
      console.error("❌ Logout error:", error);
      navigate("/", { replace: true });
    }
  };

  return (
    <header className={`main-header${headerHidden ? " header-hidden" : ""}`}>
      <div className="header-container">

        {/* Logo */}
        <div className="logo" onClick={() => window.location.href = "/"}>
          <h1>JOURNIM</h1>
        </div>

        {/* Desktop Navigation */}
        <nav className="main-nav">
          <div className="nav-item has-dropdown"
            ref={dropdownRef}
            onClick={() => setItineraryDropdown(!itineraryDropdown)}
            onMouseEnter={() => setItineraryDropdown(true)}
            onMouseLeave={() => setItineraryDropdown(false)}
          >
            <span>CREATE ITINERARY</span>
            <ChevronDown size={16} className={`chevron-icon ${itineraryDropdown ? "open" : ""}`} />
            
            {itineraryDropdown && (
              <div className="nav-dropdown">
                <div className="nav-dropdown-item" onClick={(e) => { e.stopPropagation(); setItineraryDropdown(false); window.location.href = "/itinerary"; }}>✨ Create Yourself</div>
                <div className="nav-dropdown-item" onClick={(e) => { e.stopPropagation(); setItineraryDropdown(false); navigate("/agent", { state: { startFresh: true } }); }}>🤖 Create With Agent J</div>
                <div className="nav-dropdown-item" onClick={(e) => { e.stopPropagation(); setItineraryDropdown(false); window.location.href = "/reel-to-real"; }}>🔗 Reel to Real</div>
                {/* <div className="nav-dropdown-item" onClick={(e) => { e.stopPropagation(); setItineraryDropdown(false); navigate("/budget-itinerary"); }}>💰 Budget Itinerary <span className="beta-tag">BETA</span></div> */}
              </div>
            )}
          </div>

          <div className="nav-item" onClick={() => navigate("/treks")}>
            <span>TREKS</span>
          </div>

          <div className="nav-item" onClick={() => window.location.href = "/about"}>
            <span>ABOUT US</span>
          </div>
        </nav>

        {/* Right Section */}
        <div className="header-right">
          {isSignedIn ? (
             <div className="user-profile" ref={profileDropdownRef}>
              <div
                className="user-avatar-box"
                onClick={() => setProfileDropdown(!profileDropdown)}
              >
                <div className="avatar-circle">
                  {userName.charAt(0).toUpperCase()}
                </div>
              </div>

              {profileDropdown && (
                <div className="user-dropdown">
                  <div className="dropdown-user-info">
                    <div className="dropdown-avatar">
                      {userName.charAt(0).toUpperCase()}
                    </div>
                    <span className="dropdown-username">Hey, {userName}!</span>
                  </div>
                  <div className="dropdown-divider"></div>
                  <p onClick={() => { window.location.href = "/profile"; setProfileDropdown(false); }}>Profile</p>
                  <p onClick={() => { window.location.href = "/yourtrips"; setProfileDropdown(false); }}>Your Trips</p>
                  <p onClick={() => { window.location.href = "/wishlist"; setProfileDropdown(false); }}>Wishlist</p>
                  <div className="dropdown-divider"></div>
                  <p className="logout-btn" onClick={handleLogout}>
                    Logout
                  </p>
                </div>
              )}
            </div>
          ) : (
            <button 
              className="login-btn"
              onClick={() => window.location.href = "/login"}
            >
              Login
            </button>
          )}

          {/* Hamburger Menu Icon - Mobile Only */}
          <button 
            className="hamburger-menu"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      <nav className={`mobile-nav ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        <div className="mobile-nav-item" onClick={() => setMobileItineraryOpen(!mobileItineraryOpen)}>
          <span>CREATE ITINERARY</span>
          <ChevronDown size={16} className={`chevron-icon ${mobileItineraryOpen ? "open" : ""}`} />
        </div>
        
        {mobileItineraryOpen && (
          <div className="mobile-sub-nav">
             <div onClick={() => { setMobileMenuOpen(false); window.location.href = "/itinerary"; }}>✨ Create Yourself</div>
             <div onClick={() => { setMobileMenuOpen(false); navigate("/agent", { state: { startFresh: true } }); }}>🤖 Create With Agent J</div>
             <div onClick={() => { setMobileMenuOpen(false); window.location.href = "/reel-to-real"; }}>🔗 Reel to Real</div>
             {/* <div onClick={() => { setMobileMenuOpen(false); navigate("/budget-itinerary"); }}>💰 Budget Itinerary <span className="beta-tag">BETA</span></div> */}
          </div>
        )}

        <div className="mobile-nav-item" onClick={() => { setMobileMenuOpen(false); navigate("/treks"); }}>
          <span>TREKS</span>
        </div>
        <div className="mobile-nav-item" onClick={() => { setMobileMenuOpen(false); window.location.href = "/about"; }}>
          <span>ABOUT US</span>
        </div>

        <div className="mobile-nav-divider"></div>

        {isSignedIn ? (
          <>
            <div className="mobile-nav-item" onClick={() => { window.location.href = "/profile"; setMobileMenuOpen(false); }}>
              <span>Profile</span>
            </div>
            <div className="mobile-nav-item" onClick={() => { window.location.href = "/yourtrips"; setMobileMenuOpen(false); }}>
              <span>Your Trips</span>
            </div>
            <div className="mobile-nav-item" onClick={() => { window.location.href = "/wishlist"; setMobileMenuOpen(false); }}>
              <span>Wishlist</span>
            </div>
            <div className="mobile-nav-item logout-item" onClick={() => { handleLogout(); setMobileMenuOpen(false); }}>
              <span>Logout</span>
            </div>
          </>
        ) : (
          <div className="mobile-nav-item login-item" onClick={() => { window.location.href = "/login"; setMobileMenuOpen(false); }}>
            <span>Login</span>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;